import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { LogoMark } from './LogoMark';
import { BRAND, CONTACT, FOOTER_MORE, FOOTER_NAV, LEGAL_LINK_ORDER, SOCIAL_LINKS, OPENING_HOURS } from '../../data/site';
import { getLegalDoc } from '../../data/legal';

/**
 * Derived from the documents themselves, so the column cannot advertise a
 * policy that does not exist. Company details closes the list because the
 * statutory disclosure now has a page rather than a footer sentence.
 */
const LEGAL_LINKS = [...LEGAL_LINK_ORDER.map(slug => {
  const doc = getLegalDoc(slug)!;
  return {
    to: `/legal/${doc.slug}`,
    label: doc.shortLabel
  };
}), {
  to: '/legal/company-details',
  label: 'Business details'
}];
const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook
} as const;

/**
 * Five columns in fixed order: Brand, Navigate, What we do, Legal, Opening
 * hours. The brand column spans two of the six tracks, because it carries
 * a paragraph and an address rather than a list of links.
 *
 * Legal is a column rather than a row of hyphen-separated links along the
 * bottom. Five documents is a set worth naming, and a heading plus a
 * vertical list is easier to scan and to hit than a wrapping inline row.
 * The bottom bar keeps only the copyright and registered details, which is
 * a statement rather than navigation.
 *
 * Link rows are sized by padding on the link itself rather than a gap
 * utility plus a minimum height, which would turn a five-link column into
 * 250px of mostly empty space. They carry `py-2.5` for a ~40px row: the
 * earlier `py-1.5` produced ~32px, under the 44px target of WCAG 2.5.8,
 * and these are block-level links rather than inline text so the inline
 * exception does not apply.
 */
export function SiteFooter() {
  return <footer data-component-id="C03" role="contentinfo" className="border-t border-white/10 bg-ink py-12 lg:py-16" data-id="element-299" data-mp="3oyhamu6ukt8n">
      <div className="mx-auto max-w-container px-gutter-sm sm:px-gutter-md lg:px-gutter-lg" data-id="element-300" data-mp="2o1s551ix3m7">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-6" data-id="element-301" data-mp="3tw62ko8j72p8">
          {/* Brand. Two tracks: it carries prose and an address, not links. */}
          <div className="sm:col-span-2 lg:col-span-2" data-id="element-302" data-mp="3jx6tx5nrn728">
            <LogoMark asText tone="light" data-id="element-303" data-mp="3jyrpmyltgw1o" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-cream/70" data-id="element-304" data-mp="3fvlno35qlr10">
              {BRAND.description}
            </p>
            <ul className="mt-5 space-y-1" data-id="element-305" data-mp="3b3li9v6we5x1">
              <li data-id="element-306" data-mp="365a6vg0yfncp">
                <a href={CONTACT.telephoneHref} className="flex items-center gap-2.5 py-1.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-307" data-mp="364qyapx72bbc">
                  <Phone className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-308" data-mp="2s6spe01n7738" />
                  {CONTACT.telephone}
                </a>
              </li>
              <li data-id="element-309" data-mp="1boitmhl852kq">
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 py-1.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-310" data-mp="1bo0pkysblk29">
                  <Mail className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-311" data-mp="nxeh56eelxrq" />
                  {CONTACT.email}
                </a>
              </li>
            </ul>
            <address className="mt-3 flex items-start gap-2.5 text-sm not-italic leading-relaxed text-cream/70" data-id="element-312" data-mp="xwvlnkhx65m4">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-313" data-mp="12pop851nj7wm" />
              <span data-id="element-314" data-mp="58gka0rpnlt9">
                {CONTACT.address.line1}
                <br data-id="element-315" data-mp="a7jcgkmldxjt" />
                {CONTACT.address.townOrCity}, {CONTACT.address.county}
                <br data-id="element-316" data-mp="2bwyy0ztz78qa" />
                {CONTACT.address.postcode}
              </span>
            </address>
          </div>

          {/* Navigate */}
          <nav aria-labelledby="footer-navigate-heading" data-id="element-317" data-mp="27x7zub24dqbq">
            <h2 id="footer-navigate-heading" className="mb-4 font-bebas text-xl tracking-wide text-cream" data-id="element-318" data-mp="2lnl7ecb3n30j">
              Navigate
            </h2>
            <ul data-id="element-319" data-mp="2vkzfnomvclhr">
              {FOOTER_NAV.map(item => <li key={item.label} data-id="element-320" data-mp="2vnc66q6p046f">
                  <Link to={item.to} className="block py-2.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-321" data-mp="2qmo4naku84xy">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>

          {/* More */}
          <nav aria-labelledby="footer-more-heading" data-id="element-322" data-mp="15amrvwf5tc7o">
            <h2 id="footer-more-heading" className="mb-4 font-bebas text-xl tracking-wide text-cream" data-id="element-323" data-mp="3gvvmhl11zyr6">
              What we do
            </h2>
            <ul data-id="element-324" data-mp="384d5x9073gxk">
              {FOOTER_MORE.map(item => <li key={item.label} data-id="element-325" data-mp="381qz5bl4p5ge">
                  <Link to={item.to} className="block py-2.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-326" data-mp="3c4x0q5xaq8gj">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-labelledby="footer-legal-heading" data-id="element-327" data-mp="2t8nir7j9wxr9">
            <h2 id="footer-legal-heading" className="mb-4 font-bebas text-xl tracking-wide text-cream" data-id="element-328" data-mp="nensn0f5436z">
              Legal
            </h2>
            <ul data-id="element-329" data-mp="20co28dz8seol">
              {LEGAL_LINKS.map(item => <li key={item.label} data-id="element-330" data-mp="209s1khw0fbwg">
                  <Link to={item.to} className="block py-2.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-331" data-mp="1vcs7l5cfpzlp">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>

          {/* Opening hours and social */}
          <div data-id="element-332" data-mp="2aac4doz6gogs">
            <h2 className="mb-4 font-bebas text-xl tracking-wide text-cream" data-id="element-333" data-mp="2a9sm275m7y28">
              Opening hours
            </h2>
            <dl className="space-y-1.5 text-sm" data-id="element-334" data-mp="25ag2pwdyd94e">
              {OPENING_HOURS.map(row => <div key={row.days} className="flex flex-col" data-id="element-335" data-mp="32rohsthlyblh">
                  <dt className="font-bold text-cream/80" data-id="element-336" data-mp="2y06jyeavsr4q">{row.days}</dt>
                  <dd className="text-cream-muted" data-id="element-337" data-mp="2xxtt1bn5b6fj">{row.hours}</dd>
                </div>)}
            </dl>

            {/* Real profiles now, so these are named as well as iconed.
                Facebook is where bike night is actually announced, which is
                worth saying rather than leaving someone to guess from a
                glyph. Both open in a new tab, and say so. */}
            <h2 className="mb-3 mt-8 font-bebas text-xl tracking-wide text-cream" data-id="element-338" data-mp="3j8hmzg57jedr">
              Follow us
            </h2>
            <p className="mb-3 text-sm leading-relaxed text-cream-muted" data-id="element-339" data-mp="3t8gud9pw4yp1">
              Bike night, what the truck is cooking and anything new in the
              shop all go up on Facebook first.
            </p>
            <ul className="space-y-1.5" data-id="element-340" data-mp="6tp4npkfym9h">
              {SOCIAL_LINKS.map(social => {
              const Icon = SOCIAL_ICONS[social.platform];
              return <li key={social.label} data-id="element-341" data-mp="ftua3u62ral0">
                    <a href={social.href} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-2.5 py-2.5 text-sm font-medium text-cream/80 transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-342" data-mp="fuvni5akc95w">
                      <Icon className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-343" data-mp="1622j587v8gpm" />
                      {social.label}
                      <ExternalLink className="h-3 w-3 shrink-0 text-cream-muted" aria-hidden="true" data-id="element-344" data-mp="ddddcvvv7n7j" />
                      <span className="sr-only" data-id="element-345" data-mp="ncvw5901h2y0">
                        , {BRAND.name} on {social.label}, opens in a new tab
                      </span>
                    </a>
                  </li>;
            })}
            </ul>
          </div>
        </div>

        {/* One centred line: copyright, name, rights reserved. The company
            number and registered office moved to P14 (/legal/company-details),
            which the Legal column links to — statutory disclosure has to be
            reachable, not necessarily on every page in 12px type. */}
        <div className="mt-12 border-t border-white/10 pt-6" data-id="element-346" data-mp="21ead2qdd7huc">
          <p className="text-center text-xs text-cream-muted" data-id="element-347" data-mp="2p2tkl3olbqld">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>;
}